import { useConnectionState, useRoomContext } from '@livekit/components-react'
import { ConnectionState, RoomEvent } from 'livekit-client'
import { useEffect, useRef } from 'react'

import { exchangeReconnect, JoinError } from './api'
import { report } from './diagnostics'
import { classifyReconnect, shouldProbeReconnect } from './reconnectProbe'

function isReconnecting(state: ConnectionState): boolean {
  return state === ConnectionState.Reconnecting || state === ConnectionState.SignalReconnecting
}

/**
 * After ~8s of SDK reconnect grind, ask the API once whether the class is
 * still on LiveKit. If it ended, leave immediately.
 * If the exchange succeeds, do nothing — the room is alive.
 */
export function useReconnectProbe({
  reconnectToken,
  onEnded,
}: {
  reconnectToken: string
  onEnded: () => void
}): void {
  const room = useRoomContext()
  const state = useConnectionState(room)
  const startedAt = useRef<number | null>(null)
  const probed = useRef(false)
  const resuming = useRef(false)
  const stateRef = useRef(state)
  const alive = useRef(true)
  stateRef.current = state

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  useEffect(() => {
    // Events catch a short resume cycle before React can render its intermediate state.
    const onReconnecting = () => {
      if (
        resuming.current ||
        classifyReconnect({ reconnecting: true, roomState: room.state }) !== 'resume_attempt'
      ) {
        return
      }
      resuming.current = true
      report({ reconnectToken }, { stage: 'permissions', permission: 'resume=attempt' })
    }
    const onReconnected = () => {
      if (!resuming.current) return
      resuming.current = false
      report({ reconnectToken }, { stage: 'permissions', permission: 'resume=ok' })
    }
    const onDisconnected = () => {
      if (!resuming.current) return
      report(
        { reconnectToken },
        { stage: 'permissions', outcome: 'error', permission: 'resume=fail' },
      )
      if (classifyReconnect({ reconnecting: true, roomState: room.state }) === 'full_restart') {
        report(
          { reconnectToken },
          { stage: 'permissions', outcome: 'error', permission: 'resume=full_restart' },
        )
      }
      resuming.current = false
    }

    room.on(RoomEvent.Reconnecting, onReconnecting)
    room.on(RoomEvent.SignalReconnecting, onReconnecting)
    room.on(RoomEvent.Reconnected, onReconnected)
    room.on(RoomEvent.Disconnected, onDisconnected)
    return () => {
      room.off(RoomEvent.Reconnecting, onReconnecting)
      room.off(RoomEvent.SignalReconnecting, onReconnecting)
      room.off(RoomEvent.Reconnected, onReconnected)
      room.off(RoomEvent.Disconnected, onDisconnected)
    }
  }, [room, reconnectToken])

  useEffect(() => {
    if (state === ConnectionState.Connected) {
      startedAt.current = null
      probed.current = false
      return
    }
    if (!isReconnecting(state)) return
    if (startedAt.current == null) startedAt.current = Date.now()

    const tick = window.setInterval(() => {
      const ms = Date.now() - (startedAt.current ?? Date.now())
      if (
        !shouldProbeReconnect({
          reconnecting: true,
          reconnectingMs: ms,
          alreadyProbed: probed.current,
        })
      ) {
        return
      }
      probed.current = true
      void exchangeReconnect(reconnectToken).catch((error: unknown) => {
        // Ignore after unmount or once the SDK is no longer grinding.
        // Do not cancel on Reconnecting ↔ SignalReconnecting — same episode.
        if (!alive.current || !isReconnecting(stateRef.current)) return
        if (error instanceof JoinError && error.kind === 'ended') {
          onEnded()
        }
      })
    }, 1000)

    return () => window.clearInterval(tick)
  }, [state, reconnectToken, onEnded])
}
